import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getDataPointHistory,
  listBuildingFindings,
  type DataPointNature,
  type DataPointSample,
  type Finding,
} from "@/lib/api/building";
import {
  DATA_POINT_NATURE_COLOR,
  DATA_POINT_NATURE_LABEL,
} from "@/lib/building-domain";
import {
  findingSourceLabelFact,
  SOURCE_TYPE_LABEL,
} from "@/components/building/findings/findings-helpers";
import { cardSubtle } from "@/lib/card-styles";
import { ScopedLink } from "@/lib/scoped-nav";
import { cn } from "@/lib/utils";
import {
  detectGaps,
  gapThresholdMinutes,
  GAP_FILL,
  GAP_FILL_OPACITY,
  type TimeGap,
} from "@/lib/chart-gaps";
import {
  formatSignalNumericValue,
  isBinarySignalUnit,
} from "@/lib/format-signal-value";
import {
  collapseInspectorFindings,
  findingsInWindow,
  historyChartDomain,
  historyChartTicks,
  historyWindowFromTs,
  mergeHistoryGapBands,
  HISTORY_FETCH_LIMIT,
  type HistoryChartDomain,
  type HistoryDaysOption,
} from "./data-point-history-window";

const DAYS_OPTIONS: readonly HistoryDaysOption[] = [7, 30, 90, null];

const QUIET_TOIMINTO_LINK_CLASS =
  "text-sm font-medium text-primary hover:underline";

/** Chart-context peek — observation + where, then the same named jump as the cycle-ribbon slot. */
export function InspectorHavaintoPeek({
  finding,
  scopedSourceId,
}: {
  finding: Finding;
  scopedSourceId?: string;
}) {
  const sourceLabel = findingSourceLabelFact(finding, scopedSourceId);
  const where = sourceLabel
    ? `${SOURCE_TYPE_LABEL[finding.source_type]} · ${sourceLabel}`
    : SOURCE_TYPE_LABEL[finding.source_type];
  return (
    <div className={cn(cardSubtle, "p-3 space-y-2")}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        Havainto
      </div>
      <div className="text-xs font-medium leading-snug">
        {finding.observation}
      </div>
      <div className="text-[11px] text-muted-foreground">{where}</div>
      <ScopedLink
        to="/findings"
        search={(prev) => ({
          ...prev,
          category: finding.category,
          source: finding.source_type,
          finding: finding.id,
        })}
        className={QUIET_TOIMINTO_LINK_CLASS}
      >
        Näytä Havainnoissa
      </ScopedLink>
    </div>
  );
}

function fmt_ts(iso: string, include_date: boolean): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("fi-FI", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (!include_date) return time;
  return `${d.toLocaleDateString("fi-FI")} ${time}`;
}

function fmt_axis_label(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}.`;
}

function is_future(iso: string): boolean {
  return new Date(iso).getTime() > Date.now();
}

interface ChartPoint {
  t: number;
  x: number;
  recorded_at: string;
  value: number | null;
  future: boolean;
}

interface Props {
  buildingId: string;
  sourceId: string;
  sourceName: string;
  signalName: string;
  label: string;
  unit: string | null;
  nature: DataPointNature;
  onClose: () => void;
}

export function DataPointHistoryPanel({
  buildingId,
  sourceId,
  sourceName,
  signalName,
  label,
  unit,
  nature,
  onClose,
}: Props) {
  const [days, set_days] = useState<HistoryDaysOption>(null);
  const [samples, set_samples] = useState<DataPointSample[]>([]);
  const [findings, set_findings] = useState<Finding[]>([]);
  const [window_from_ts, set_window_from_ts] = useState<string | undefined>(
    undefined,
  );
  const [chart_domain, set_chart_domain] = useState<
    HistoryChartDomain | undefined
  >(undefined);
  const [show_findings, set_show_findings] = useState(true);
  const [loading, set_loading] = useState(true);

  const load = useCallback(async () => {
    set_loading(true);
    const now = new Date();
    const from_ts = historyWindowFromTs(days, now);
    const domain = historyChartDomain(days, now);
    const [fetched, fetched_findings] = await Promise.all([
      getDataPointHistory(
        buildingId,
        [sourceId],
        [signalName],
        from_ts,
        HISTORY_FETCH_LIMIT,
      ).catch((): DataPointSample[] => []),
      listBuildingFindings(buildingId, {
        sourceId,
        dataPointName: signalName,
      }).catch((): Finding[] => []),
    ]);
    set_samples(fetched);
    set_findings(fetched_findings);
    set_window_from_ts(from_ts);
    set_chart_domain(domain);
    set_loading(false);
  }, [buildingId, sourceId, signalName, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const color = DATA_POINT_NATURE_COLOR[nature] ?? "#64748b";
  const now_iso = new Date().toISOString();

  const chart_model = useMemo(() => {
    const pts: Array<{
      t: number;
      recorded_at: string;
      value: number | null;
      future: boolean;
    }> = samples
      .filter((s) => s.numeric_value != null)
      .map((s) => ({
        t: Date.parse(s.recorded_at),
        recorded_at: s.recorded_at,
        value: s.numeric_value as number,
        future: is_future(s.recorded_at),
      }))
      .sort((a, b) => a.t - b.t);

    if (pts.length === 0) {
      return {
        points: [] as ChartPoint[],
        gap_bands: [] as ReturnType<typeof mergeHistoryGapBands>,
        origin_ms: 0,
        x_domain: [0, 1] as [number, number],
        x_ticks: undefined as number[] | undefined,
      };
    }

    // Relative X (ms from window start) — epoch timestamps as Recharts numbers
    // mis-scale, so a gap's hole and the line through it land in the wrong place.
    const origin_ms = chart_domain?.fromMs ?? pts[0].t;
    const end_ms = chart_domain?.toMs ?? pts[pts.length - 1].t;
    const to_x = (ms: number) => ms - origin_ms;

    const gaps: TimeGap[] = detectGaps(pts, [signalName], (p) => p.recorded_at);
    const leading_ms = pts[0].t - origin_ms;
    if (
      chart_domain != null &&
      leading_ms > gapThresholdMinutes(signalName) * 60_000
    ) {
      gaps.unshift({
        from: new Date(origin_ms).toISOString(),
        to: pts[0].recorded_at,
        marker: new Date((origin_ms + pts[0].t) / 2).toISOString(),
      });
    }

    const marked = [...pts];
    for (const g of gaps) {
      const a = Date.parse(g.from);
      const b = Date.parse(g.to);
      for (let k = 1; k <= 5; k++) {
        const ts = new Date(a + ((b - a) * k) / 6).toISOString();
        marked.push({
          t: Date.parse(ts),
          recorded_at: ts,
          value: null,
          future: false,
        });
      }
    }
    marked.sort((a, b) => a.t - b.t);

    const points: ChartPoint[] = marked.map((p) => ({
      ...p,
      x: to_x(p.t),
    }));
    if (chart_domain != null) {
      if (points[0].x > 0) {
        points.unshift({
          t: origin_ms,
          x: 0,
          recorded_at: new Date(origin_ms).toISOString(),
          value: null,
          future: false,
        });
      }
      const span = to_x(end_ms);
      if (points[points.length - 1].x < span) {
        points.push({
          t: end_ms,
          x: span,
          recorded_at: new Date(end_ms).toISOString(),
          value: null,
          future: false,
        });
      }
    }

    return {
      points,
      gap_bands: mergeHistoryGapBands(gaps, origin_ms, end_ms),
      origin_ms,
      x_domain: [0, to_x(end_ms)] as [number, number],
      x_ticks: historyChartTicks({ fromMs: origin_ms, toMs: end_ms }).map(to_x),
    };
  }, [samples, chart_domain, signalName]);

  const has_future = chart_model.points.some((p) => p.future);

  // Same window for the Havainnot list (bug #1579). Chart markers for findings
  // are omitted here — a full-height dashed line reads as a second data-break
  // glyph and collides with the gap bands.
  const windowed_findings = useMemo(
    () =>
      collapseInspectorFindings(findingsInWindow(findings, window_from_ts)),
    [findings, window_from_ts],
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <p className="text-xs text-muted-foreground">{sourceName}</p>
            <h2 className="text-base font-semibold">{label}</h2>
            <span
              className="mt-0.5 inline-block rounded px-1.5 py-0.5 text-xs"
              style={{ backgroundColor: color + "22", color }}
            >
              {DATA_POINT_NATURE_LABEL[nature]}
            </span>
          </div>
          <button
            onClick={onClose}
            className="ml-4 rounded px-2 py-1 text-sm text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        {/* Day range selector + annotation toggle */}
        <div className="flex items-center gap-1 border-b px-5 py-2">
          {DAYS_OPTIONS.map((d) => (
            <button
              key={d ?? "all"}
              onClick={() => set_days(d)}
              className="rounded px-2.5 py-1 text-xs font-medium transition-colors"
              style={
                days === d
                  ? { backgroundColor: color + "33", color }
                  : { color: "var(--muted-foreground)" }
              }
            >
              {d != null ? `${d}pv` : "Kaikki"}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1.5">
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground select-none">
              <input
                type="checkbox"
                checked={show_findings}
                onChange={(e) => set_show_findings(e.target.checked)}
                className="h-3 w-3 rounded"
              />
              Näytä havainnot{" "}
              {windowed_findings.length > 0 && `(${windowed_findings.length})`}
            </label>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-muted-foreground">Ladataan…</p>
          </div>
        ) : (
          <div className="flex-1 space-y-6 p-5">
            {/* Numeric chart */}
            {chart_model.points.length > 0 && (
              <section>
                <h3 className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Aikasarja{" "}
                  {unit && !isBinarySignalUnit(unit) ? `(${unit})` : ""}
                </h3>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart
                    data={chart_model.points}
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--border)"
                    />
                    <XAxis
                      dataKey="x"
                      type="number"
                      domain={chart_model.x_domain}
                      ticks={chart_model.x_ticks}
                      tickFormatter={(x: number) =>
                        fmt_axis_label(
                          new Date(chart_model.origin_ms + x).toISOString(),
                        )
                      }
                      tick={{ fontSize: 10 }}
                      allowDataOverflow
                    />
                    <YAxis tick={{ fontSize: 10 }} width={40} />
                    <Tooltip
                      labelFormatter={(v) =>
                        fmt_ts(
                          new Date(
                            chart_model.origin_ms + Number(v),
                          ).toISOString(),
                          true,
                        )
                      }
                      formatter={(v) => [
                        v == null
                          ? "—"
                          : formatSignalNumericValue(v as number, unit, nature),
                        label,
                      ]}
                      contentStyle={{ fontSize: 11 }}
                    />
                    {chart_model.gap_bands.map((band) => (
                      <ReferenceArea
                        key={`${band.x1}-${band.x2}`}
                        x1={band.x1}
                        x2={band.x2}
                        fill={GAP_FILL}
                        fillOpacity={GAP_FILL_OPACITY}
                        stroke="none"
                        ifOverflow="hidden"
                      />
                    ))}
                    {has_future && (
                      <ReferenceLine
                        x={Date.parse(now_iso) - chart_model.origin_ms}
                        stroke={color}
                        strokeDasharray="4 2"
                        label={{ value: "nyt", fill: color, fontSize: 10 }}
                      />
                    )}
                    <Line
                      dataKey="value"
                      stroke={color}
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </section>
            )}

            {/* Findings linked to this data point, scoped to the selected window */}
            {show_findings && windowed_findings.length > 0 && (
              <section>
                <h3 className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Havainnot
                </h3>
                <ul className="space-y-2">
                  {windowed_findings.map((finding) => (
                    <li key={finding.id}>
                      <InspectorHavaintoPeek
                        finding={finding}
                        scopedSourceId={sourceId}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {chart_model.points.length === 0 && (
              <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                Ei historiaa valitulla aikavälillä
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
